import DOMPurify from "isomorphic-dompurify";
import { token } from "@stubhub/broadway-foundation/tokens";
import { FanProtectFillIcon, TicketMoneyOutlineIcon } from "@stubhub/broadway-web/icons/functional";
import { useContent, useFormattedContent } from "src/shared/components/content";
import { useAppContext } from "src/shared/context/AppContext";
import { useIsNewZealand, useIsStubhub } from "src/shared/utils/appContextHelper";
import { UrlConfigurationType, writeAbsoluteUrl } from "src/shared/utils/url-builder";
import {
  FAN_PROTECT_TRADEMARK,
  VIAGOGO_GUARANTEE_CONTENT_ID,
} from "src/website-apps/shared/components/brand-guarantee/constants";
import { linkStyleClass } from "./styles";
import type { GuaranteeHighlightData } from "./types";

interface GuaranteeHighlightsOptions {
  isEligibleForResale: boolean;
}

/**
 * Returns FanProtect + optional Resell Anytime highlight data, matching the
 * event-page guarantee highlights exactly (content IDs, icons, link styling).
 *
 * Extracted to shared so both event and performer pages can render identical
 * trust messaging without cross-app imports.
 */
export const useGuaranteeHighlights = ({
  isEligibleForResale,
}: GuaranteeHighlightsOptions): GuaranteeHighlightData[] => {
  const appContext = useAppContext();
  const fanProtectDescription = useContent("cid$#4041999#");
  const viagogoGuaranteeTitle = useContent(VIAGOGO_GUARANTEE_CONTENT_ID);

  const supportArticleUrl = writeAbsoluteUrl(
    "/articles/61000276294-reselling-tickets-bought-on-stubhub",
    null,
    "",
    UrlConfigurationType.SUPPORT,
    appContext,
  );
  const resellAnytimeDescription = useFormattedContent("cid$#3580168#", supportArticleUrl);
  const resellAnytimeTitle = useContent("cid$#3747785#"); // "Resell Anytime"

  const isStubhub = useIsStubhub();
  const isNewZealand = useIsNewZealand();
  const guaranteeTitle = isStubhub ? FAN_PROTECT_TRADEMARK : viagogoGuaranteeTitle;

  const fanProtectHighlight: GuaranteeHighlightData[] = isNewZealand
    ? []
    : [
        {
          messageText: guaranteeTitle,
          icon: <FanProtectFillIcon size={24} color={token("colors.bg.fill.info")} />,
          analyticsItemId: "brandGuarantee",
          qualifier: fanProtectDescription,
        },
      ];

  const resellAnytimeHighlight: GuaranteeHighlightData[] = isEligibleForResale
    ? [
        {
          messageText: resellAnytimeTitle,
          icon: <TicketMoneyOutlineIcon size={24} />,
          analyticsItemId: "resellAnytimeGuarantee",
          qualifier: (
            <span
              className={linkStyleClass}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(resellAnytimeDescription, { ALLOWED_ATTR: ["target", "href"] }),
              }}
            />
          ),
        },
      ]
    : [];

  return [...fanProtectHighlight, ...resellAnytimeHighlight];
};
